import React from 'react';
import { motion } from 'framer-motion';
import { Star, Calendar, MapPin, Clock, ArrowRight, ShieldCheck, CheckCircle2, Info } from 'lucide-react';
import { useCartStore } from '../../store/cartStore';

const BirthChart: React.FC = () => {
  const { addItem } = useCartStore();
  const [formData, setFormData] = React.useState({
    name: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    gender: 'Other'
  });

  const handlePurchase = (e: React.FormEvent) => {
    e.preventDefault();
    addItem({
      id: 'bc-reading',
      name: 'Personalized Birth Chart Reading',
      price: 1499,
      quantity: 1,
      image: 'https://cdn.pixabay.com/photo/2015/10/17/19/30/astrology-993127_1280.jpg',
      type: 'service'
    });
    alert('Added to cart! Complete the checkout to receive your reading.');
  };

  return (
    <div className="min-h-screen bg-midnight py-24 noise-overlay">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-start">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-12"
          >
            <div>
              <span className="text-gold text-[10px] font-bold uppercase tracking-[0.3em] mb-6 block">Premium Service</span>
              <h1 className="text-5xl md:text-7xl font-serif font-bold mb-8 leading-tight">Your Life's <br /><span className="text-gold italic font-medium">Cosmic Blueprint</span></h1>
              <p className="text-gray-400 text-lg leading-relaxed font-light">
                Our personalized birth chart reading is a comprehensive 40-page digital report that decodes the exact position of the planets at the moment of your birth.
              </p>
            </div>

            <div className="space-y-8">
              {[
                { icon: <Star className="w-6 h-6 text-gold" />, title: "Personality Analysis", desc: "Deep dive into your sun, moon, and rising signs." },
                { icon: <Calendar className="w-6 h-6 text-gold" />, title: "Future Forecast", desc: "12-month planetary transit predictions." },
                { icon: <ShieldCheck className="w-6 h-6 text-gold" />, title: "Karmic Path", desc: "Understanding your past life influences and soul's purpose." }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-6 group">
                  <div className="w-12 h-12 rounded-xl bg-gold/5 border border-gold/10 flex items-center justify-center shrink-0 group-hover:border-gold/30 transition-all duration-300">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-serif font-bold text-xl mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-sm font-light leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-10 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 blur-3xl rounded-full -mr-16 -mt-16" />
              <div className="flex justify-between items-center mb-6">
                <span className="text-white font-bold text-lg">Investment</span>
                <span className="text-3xl text-gold font-serif font-bold">₹1,499</span>
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-500 mb-0 italic">
                <CheckCircle2 className="w-4 h-4 text-gold" />
                <span>Delivery: Digital PDF via Email within 24 hours.</span>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white/5 border border-white/10 p-10 md:p-16 rounded-[40px] backdrop-blur-2xl shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 blur-[100px] rounded-full -mr-32 -mt-32" />
            
            <div className="mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Enter Birth Details</h2>
              <p className="text-gray-500 text-sm font-light">Please provide accurate information for the most precise reading.</p>
            </div>

            <form onSubmit={handlePurchase} className="space-y-8">
              <div className="space-y-3">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Full Name</label>
                <input 
                  type="text" 
                  required
                  className="w-full px-8 py-5 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:border-gold transition-all text-white placeholder:text-gray-700"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Birth Date</label>
                  <input 
                    type="date" 
                    required
                    className="w-full px-8 py-5 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:border-gold transition-all text-white"
                    value={formData.birthDate}
                    onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Birth Time</label>
                  <input 
                    type="time" 
                    required
                    className="w-full px-8 py-5 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:border-gold transition-all text-white"
                    value={formData.birthTime}
                    onChange={(e) => setFormData({...formData, birthTime: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Birth Place</label>
                <div className="relative">
                  <MapPin className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gold" />
                  <input 
                    type="text" 
                    required
                    className="w-full pl-16 pr-8 py-5 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:border-gold transition-all text-white placeholder:text-gray-700"
                    placeholder="City, Country"
                    value={formData.birthPlace}
                    onChange={(e) => setFormData({...formData, birthPlace: e.target.value})}
                  />
                </div>
              </div>

              <button 
                type="submit"
                className="w-full py-6 bg-gold text-midnight font-bold rounded-2xl hover:bg-white transition-all duration-500 flex items-center justify-center group shadow-2xl shadow-gold/20"
              >
                Purchase Reading
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </button>
            </form>

            <div className="mt-12 flex items-center justify-center space-x-8 text-[10px] text-gray-600 uppercase tracking-[0.2em]">
              <div className="flex items-center">
                <ShieldCheck className="w-4 h-4 mr-2 text-green-500" />
                Secure Checkout
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-gold" />
                24h Delivery
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <div className="mt-32 py-24 border-t border-white/5">
          <h2 className="text-3xl font-serif font-bold mb-16 text-center">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {[
              { q: "What data do you need?", a: "We need your exact birth date, time, and location to calculate the precise planetary alignments." },
              { q: "How long is the report?", a: "Our standard birth chart reading is approximately 40 pages of detailed analysis and forecasts." },
              { q: "Is my data private?", a: "Absolutely. We use your birth details only to generate your report and never share them with third parties." },
              { q: "Can I ask follow-up questions?", a: "Yes, every birth chart purchase includes a 10% discount on a live consultation with our experts." }
            ].map((faq, idx) => (
              <div key={idx} className="space-y-4">
                <h4 className="text-white font-serif font-bold text-lg flex items-center">
                  <Info className="w-4 h-4 mr-3 text-gold" />
                  {faq.q}
                </h4>
                <p className="text-gray-500 text-sm font-light leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthChart;
